import { createApiClient, isProblemDetails, type ProblemDetails } from '@jervis/api-client';
import type { Middleware } from 'openapi-fetch';

/** Reads a readable (non-httpOnly) cookie by name; `document.cookie` is unavailable in SSR/tests. */
function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1] as string) : undefined;
}

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

/** Registered once from `main.ts` after the router/session store exist, to avoid a circular import. */
export function onUnauthorized(handler: UnauthorizedHandler): void {
  unauthorizedHandler = handler;
}

const AUTH_PATHS = ['/auth/login', '/auth/logout', '/auth/session'];

const unauthorizedMiddleware: Middleware = {
  onResponse({ request, response }) {
    if (response.status === 401 && !AUTH_PATHS.some((path) => request.url.includes(path))) {
      unauthorizedHandler?.();
    }
    return response;
  },
};

/** The header the API's IAM guard reads the selected company from (`platform/iam/src/iam.options.ts`
 * `COMPANY_HEADER`, checked against the principal's own scopes server-side — this is a UX convenience,
 * not a trust boundary). */
const COMPANY_HEADER = 'x-company-id';

type CompanyIdSupplier = () => string | null | undefined;
let companyIdSupplier: CompanyIdSupplier | null = null;

/** Registered once from the session store so every request carries the currently selected company —
 * without this header the API never learns which company a multi-company principal means, and every
 * company-scoped read/write silently falls back to "no company" (WP7.9b). */
export function onCompanyIdRequest(supplier: CompanyIdSupplier): void {
  companyIdSupplier = supplier;
}

/** Stamps the selected company on the outgoing request. Done in the transport (not as an `onRequest`
 * middleware) so it adds no extra awaited step before `fetch` is invoked. */
function withCompanyHeader(request: Request): Request {
  const companyId = companyIdSupplier?.();
  if (companyId) {
    request.headers.set(COMPANY_HEADER, companyId);
  }
  return request;
}

/**
 * Same-origin client: the dev server and production reverse proxy forward /api, /health and /ready.
 * `fetch` is resolved per request so tests and the desktop shell can substitute the transport.
 * CSRF token is read straight from the readable `jervis_csrf` cookie set at login (ADR-024 double submit).
 */
export const api = createApiClient({
  fetch: (request) => globalThis.fetch(withCompanyHeader(request)),
  csrfToken: () => readCookie('jervis_csrf'),
});

api.use(unauthorizedMiddleware);

/** A create-command header: pass as `{ headers: idempotencyHeader() }` on POSTs that create a resource. */
export function idempotencyHeader(): Record<string, string> {
  return { 'Idempotency-Key': crypto.randomUUID() };
}

/** One replayed command's outcome (see `desktop-bridge.ts` `OutboxReplayOutcome`). */
export interface ReplayResponse {
  ok: boolean;
  status: number;
  /** `<problem code>: <detail>` for a non-2xx RFC 9457 response, shown on the FAILED-queue screen. */
  detail?: string;
}

/**
 * Replays one queued desktop command through the existing REST endpoint with the SAME session/CSRF/
 * company handling as `api` and the command's client-generated `Idempotency-Key` (ADR-085). A network
 * failure rejects (the caller reverts the command to PENDING); an HTTP response — including a 4xx —
 * resolves with its status so the caller can decide PENDING vs FAILED.
 */
export async function sendQueuedCommand(
  method: string,
  path: string,
  bodyJson: string,
  idempotencyKey: string,
  fetchImpl: typeof fetch = (input, init) => globalThis.fetch(input, init),
): Promise<ReplayResponse> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    'idempotency-key': idempotencyKey,
  };
  const csrf = readCookie('jervis_csrf');
  if (csrf) headers['x-csrf-token'] = csrf;
  const companyId = companyIdSupplier?.();
  if (companyId) headers[COMPANY_HEADER] = companyId;
  const response = await fetchImpl(path, { method, credentials: 'include', headers, body: bodyJson });
  if (response.status === 401) unauthorizedHandler?.();
  if (response.ok) return { ok: true, status: response.status };
  let detail: string | undefined;
  try {
    const problem = (await response.json()) as Partial<ProblemDetails> | null;
    if (problem && typeof problem === 'object') {
      const code = problem.type?.split('/').pop();
      const text = problem.detail ?? problem.title;
      detail = [code, text].filter(Boolean).join(': ') || undefined;
    }
  } catch {
    // Non-JSON error body (proxy error page, ...): the status alone is reported.
  }
  return { ok: false, status: response.status, ...(detail ? { detail } : {}) };
}

export type { ProblemDetails };
export { isProblemDetails };

/** Field-level validation errors keyed by dotted path, from a `validation-failed` problem response. */
export function fieldErrors(problem: ProblemDetails | undefined): Record<string, string> {
  if (!problem || !Array.isArray(problem.errors)) return {};
  const out: Record<string, string> = {};
  for (const entry of problem.errors as unknown[]) {
    if (
      entry &&
      typeof entry === 'object' &&
      'path' in entry &&
      'message' in entry &&
      typeof (entry as { path: unknown }).path === 'string'
    ) {
      const path = (entry as { path: string }).path;
      const message = String((entry as { message: unknown }).message);
      if (path) out[path] = message;
    }
  }
  return out;
}

/** Stable error code from the problem `type` URI (`https://errors.jervis-erp.dev/<code>`). */
export function problemCode(problem: ProblemDetails | undefined): string | undefined {
  return problem?.type?.split('/').pop();
}

/**
 * Raw multipart POST for the one endpoint the typed client cannot express: `/imports/batches`
 * reads its file via Fastify's `request.file()` (ADR-027), so the generated OpenAPI operation has
 * `requestBody: never` and openapi-fetch refuses a `body`. This mirrors `createApiClient`'s own
 * cookie-based session/CSRF handling (same-origin, `credentials: include`) for that one call.
 */
export async function postMultipart<T = unknown>(
  path: string,
  formData: FormData,
): Promise<{ data?: T; error?: ProblemDetails }> {
  const headers: Record<string, string> = {};
  const csrf = readCookie('jervis_csrf');
  if (csrf) headers['x-csrf-token'] = csrf;
  const companyId = companyIdSupplier?.();
  if (companyId) headers[COMPANY_HEADER] = companyId;
  let response: Response;
  try {
    response = await globalThis.fetch(path, { method: 'POST', credentials: 'include', headers, body: formData });
  } catch {
    return {
      error: {
        type: 'https://errors.jervis-erp.dev/network-error',
        title: 'Network error',
        status: 0,
        instance: path,
        correlationId: '',
      },
    };
  }
  if (response.status === 401) unauthorizedHandler?.();
  const contentType = response.headers.get('content-type') ?? '';
  const body: unknown = contentType.includes('json') ? await response.json() : undefined;
  if (!response.ok) return { error: body as ProblemDetails };
  return { data: body as T };
}
